import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, FileText, Download, Upload, Trash2, Building2, User, Eye, ArrowLeft, Edit, Archive } from 'lucide-react';
import { axiosInstance } from '@/App';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SDSLookup = ({ userRole }) => {
  const { t } = useTranslation();
  // Get user from localStorage to check permissions
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [sdsDocuments, setSdsDocuments] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    files: [],  // For bulk upload
    chemical_name: '',
    manufacturer: '',
    product_name: '',
    cas_number: ''
  });
  
  const [editingSds, setEditingSds] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    manufacturer: '',
    cas_number: '',
    product_name: ''
  });
  const [isDragging, setIsDragging] = useState(false);
  const [showSupersededDialog, setShowSupersededDialog] = useState(false);
  const [supersededDocs, setSupersededDocs] = useState([]);
  const [loadingSuperseded, setLoadingSuperseded] = useState(false);

  const canManageSds = (
    userRole === 'admin' ||
    currentUser?.role === 'company_admin' ||
    currentUser?.role === 'super_admin'
  );

  const loadSuperseded = async () => {
    try {
      setLoadingSuperseded(true);
      const r = await axiosInstance.get('/sds/superseded');
      setSupersededDocs(r.data.superseded_documents || []);
    } catch (e) {
      console.error('Failed to load superseded SDS:', e);
      toast.error('Failed to load superseded SDS');
    } finally {
      setLoadingSuperseded(false);
    }
  };

  const handleRestoreSuperseded = async (sdsId, label) => {
    try {
      const r = await axiosInstance.post(`/sds/${sdsId}/restore`);
      toast.success(r.data?.message || `Restored ${label}`);
      await loadSuperseded();
      await loadSDSLibrary(searchQuery);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore SDS');
    }
  };

  useEffect(() => {
    // Don't load all documents on mount - wait for user to search
    // loadSDSLibrary();
  }, []);

  const loadSDSLibrary = async (search = '') => {
    try {
      setSearching(true);
      const response = await axiosInstance.get('/sds/library', {
        params: { search }
      });
      setSdsDocuments(response.data.sds_documents);
    } catch (error) {
      console.error('Failed to load SDS library:', error);
      toast.error('Failed to load SDS library');
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = () => {
    loadSDSLibrary(searchQuery);
  };

  const cleanFileName = (fileName) => {
    // Remove .pdf extension and common SDS suffixes
    return fileName
      .replace(/\.pdf$/i, '')
      .replace(/[_-](SDS|MSDS|Safety|Data|Sheet)$/i, '')
      .replace(/_/g, ' ')
      .trim();
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const pdfFiles = selectedFiles.filter(file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
    
    if (pdfFiles.length === 0) {
      toast.error('Please select at least one PDF file');
      e.target.value = '';
      return;
    }
    
    if (pdfFiles.length !== selectedFiles.length) {
      toast.warning(`${selectedFiles.length - pdfFiles.length} non-PDF file(s) were skipped`);
    }
    
    // For single file, populate chemical name from filename
    if (pdfFiles.length === 1) {
      const cleanName = cleanFileName(pdfFiles[0].name);
      setUploadForm({ 
        ...uploadForm, 
        file: pdfFiles[0],
        files: pdfFiles,
        chemical_name: uploadForm.chemical_name || cleanName 
      });
    } else {
      setUploadForm({ 
        ...uploadForm, 
        file: null,
        files: pdfFiles,
        chemical_name: '' 
      });
    }
    
    toast.info(`📁 ${pdfFiles.length} PDF file(s) selected for upload`);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const getAllFileEntries = async (dataTransferItemList) => {
    let fileEntries = [];
    let queue = [];
    
    // Convert items to entries
    for (let i = 0; i < dataTransferItemList.length; i++) {
      const item = dataTransferItemList[i];
      console.log('Item:', item.kind, item);
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : item.getAsEntry();
        console.log('Entry:', entry);
        if (entry) {
          queue.push(entry);
        }
      }
    }
    
    console.log('Initial queue:', queue.length, 'items');
    
    // Process queue (handle folders recursively)
    while (queue.length > 0) {
      let entry = queue.shift();
      console.log('Processing entry:', entry.name, entry.isFile ? 'FILE' : 'DIRECTORY');
      
      if (entry.isFile) {
        fileEntries.push(entry);
      } else if (entry.isDirectory) {
        let reader = entry.createReader();
        let entries = await new Promise((resolve, reject) => {
          reader.readEntries(
            (entries) => {
              console.log(`Found ${entries.length} entries in folder ${entry.name}`);
              resolve(entries);
            },
            (error) => {
              console.error('Error reading directory:', error);
              reject(error);
            }
          );
        });
        queue.push(...entries);
      }
    }
    
    console.log('Total file entries found:', fileEntries.length);
    return fileEntries;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    console.log('Drop event triggered');
    toast.info('Processing dropped files...');
    
    try {
      const items = e.dataTransfer.items;
      console.log('DataTransfer items:', items ? items.length : 0);
      
      if (!items || items.length === 0) {
        // Fallback to files if items not available
        const files = Array.from(e.dataTransfer.files);
        console.log('Using fallback files:', files.length);
        
        const pdfFiles = files.filter(file => 
          file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
        );
        
        if (pdfFiles.length === 0) {
          toast.error('No PDF files found in the dropped folder/files');
          return;
        }
        
        setUploadForm({
          ...uploadForm,
          file: null,
          files: pdfFiles,
          chemical_name: ''
        });
        
        toast.success(`📁 Loaded ${pdfFiles.length} PDF files ready for upload!`);
        return;
      }
      
      // Get all file entries (handles folders recursively)
      const fileEntries = await getAllFileEntries(items);
      console.log('File entries processed:', fileEntries.length);
      
      // Convert file entries to actual File objects
      const filePromises = fileEntries.map(entry => {
        return new Promise((resolve, reject) => {
          entry.file(resolve, reject);
        });
      });
      
      const allFiles = await Promise.all(filePromises);
      console.log('Converted to File objects:', allFiles.length);
      
      // Filter for PDF files only
      const pdfFiles = allFiles.filter(file => 
        file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      );
      
      console.log('PDF files found:', pdfFiles.length);
      
      if (pdfFiles.length === 0) {
        toast.error('No PDF files found in the dropped folder/files');
        return;
      }
      
      if (pdfFiles.length !== allFiles.length) {
        toast.info(`Found ${pdfFiles.length} PDF files (${allFiles.length - pdfFiles.length} non-PDF files skipped)`);
      }
      
      setUploadForm({
        ...uploadForm,
        file: null,
        files: pdfFiles,
        chemical_name: ''
      });
      
      toast.success(`📁 Loaded ${pdfFiles.length} PDF files ready for upload!`);
    } catch (error) {
      console.error('Error processing dropped files:', error);
      toast.error(`Failed to process dropped files: ${error.message}`);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    const filesToUpload = uploadForm.files.length > 0 ? uploadForm.files : [uploadForm.file];
    
    if (filesToUpload.length === 0 || !filesToUpload[0]) {
      toast.error('Please select at least one PDF file');
      return;
    }

    // For single file upload, require chemical name
    if (filesToUpload.length === 1 && !uploadForm.chemical_name) {
      toast.error('Please provide the chemical name');
      return;
    }

    setUploading(true);
    
    try {
      const totalFiles = filesToUpload.length;
      
      // Show initial progress
      toast.info(`Uploading ${totalFiles} file(s)...`, { id: 'upload-progress' });
      
      // Upload files in parallel (max 3 at a time for better performance)
      const uploadPromises = filesToUpload.map(async (file, index) => {
        const formData = new FormData();
        formData.append('file', file);
        
        // For bulk upload, use cleaned filename as chemical name if not provided
        const chemicalName = filesToUpload.length === 1 
          ? uploadForm.chemical_name 
          : (uploadForm.chemical_name || cleanFileName(file.name));
        
        formData.append('chemical_name', chemicalName);
        if (uploadForm.manufacturer) formData.append('manufacturer', uploadForm.manufacturer);
        if (uploadForm.product_name) formData.append('product_name', uploadForm.product_name);
        if (uploadForm.cas_number) formData.append('cas_number', uploadForm.cas_number);

        try {
          const uploadResp = await axiosInstance.post('/sds/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (uploadResp.data?.extraction_warning) {
            toast.warning(`${file.name}: ${uploadResp.data.extraction_warning}`, { duration: 9000 });
          }
          
          return { success: true, file: file.name, chemicalName };
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          
          // Extract the specific error message from the backend
          const errorMessage = error.response?.data?.detail || error.message || 'Upload failed';
          const shortReason = errorMessage.includes('already exists') ? 'already exists in library' 
            : errorMessage.includes('newer') ? 'newer version already exists'
            : errorMessage.includes('same version') ? 'same version already exists'
            : errorMessage.includes('revision date') ? 'revision date conflict'
            : 'upload failed';
          
          return { 
            success: false, 
            file: file.name, 
            chemicalName,
            reason: shortReason
          };
        }
      });

      // Execute uploads in batches of 3 for optimal performance
      const batchSize = 3;
      const results = [];
      for (let i = 0; i < uploadPromises.length; i += batchSize) {
        const batch = uploadPromises.slice(i, i + batchSize);
        const batchResults = await Promise.all(batch);
        results.push(...batchResults);
      }

      // Dismiss progress toast
      toast.dismiss('upload-progress');

      // Count results
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      const failedFiles = results.filter(r => !r.success);

      // Show final summary
      if (successCount > 0 && failCount === 0) {
        toast.success(`🎉 All ${successCount} SDS document(s) uploaded successfully!`, { duration: 6000 });
      } else if (successCount > 0 && failCount > 0) {
        // Build detailed message for mixed results
        let message = `✅ ${successCount} uploaded, ❌ ${failCount} rejected:\n\n`;
        failedFiles.forEach(failed => {
          message += `• ${failed.chemicalName}: ${failed.reason}\n`;
        });
        
        toast.info(message, { 
          duration: 8000,
          style: { whiteSpace: 'pre-line', maxWidth: '500px' }
        });
      } else {
        // All failed - show reasons
        let message = `❌ All ${failCount} file(s) rejected:\n\n`;
        failedFiles.slice(0, 5).forEach(failed => {
          message += `• ${failed.chemicalName}: ${failed.reason}\n`;
        });
        if (failedFiles.length > 5) {
          message += `\n...and ${failedFiles.length - 5} more`;
        }
        
        toast.error(message, { 
          duration: 8000,
          style: { whiteSpace: 'pre-line', maxWidth: '500px' }
        });
      }
      
      setShowUploadDialog(false);
      setUploadForm({
        file: null,
        files: [],
        chemical_name: '',
        manufacturer: '',
        product_name: '',
        cas_number: ''
      });
      
      // Trigger background processing automatically
      if (successCount > 0) {
        toast.info('🔄 Processing metadata in background...', { duration: 3000, id: 'bg-process' });
        
        setTimeout(async () => {
          try {
            const response = await axiosInstance.post('/sds/process-pending');
            const data = response.data;
            
            toast.dismiss('bg-process');
            
            // Show final results
            let finalMessage = `🎉 Processing complete!\n\n✅ ${data.added} SDS added`;
            if (data.rejected > 0) {
              finalMessage += `\n❌ ${data.rejected} rejected (duplicates)`;
            }
            
            toast.success(finalMessage, { 
              duration: 8000,
              style: { whiteSpace: 'pre-line', maxWidth: '500px' }
            });
            
            loadSDSLibrary();
          } catch (error) {
            console.error('Background processing failed:', error);
            toast.dismiss('bg-process');
            toast.error('Background processing failed. Please refresh the page.');
            loadSDSLibrary();
          }
        }, 2000);
      } else {
        loadSDSLibrary();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload SDS documents');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (sds) => {
    try {
      toast.info('Downloading SDS...');
      const response = await axiosInstance.get(`/sds/download/${sds.id}`, {
        responseType: 'blob'
      });
      
      // Check if we got actual data
      if (!response.data || response.data.size === 0) {
        toast.error('Downloaded file is empty. The SDS may need to be re-uploaded.');
        return;
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Use chemical_name or product_name as filename fallback
      const filename = sds.filename || `${sds.chemical_name || sds.product_name || 'SDS'}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('SDS downloaded');
    } catch (error) {
      console.error('Download failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to download SDS';
      toast.error(errorMsg, { duration: 5000 });
    }
  };

  const handleView = async (sds) => {
    try {
      const response = await axiosInstance.get(`/sds/download/${sds.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      console.error('View failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to view SDS';
      toast.error(errorMsg, { duration: 5000 });
    }
  };

  const handleDelete = async (sds) => {
    if (!window.confirm(`Are you sure you want to delete the SDS for ${sds.chemical_name}?`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/sds/${sds.id}`);
      toast.success('SDS deleted successfully');
      loadSDSLibrary();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete SDS');
    }
  };

  const canDelete = (sds) => {
    if (!currentUser) return false;
    return currentUser.role === 'super_admin' || sds.uploaded_by?.user_id === currentUser.id;
  };

  const canEdit = (sds) => {
    return userRole === 'company_admin' || userRole === 'super_admin';
  };

  const openEditDialog = (sds) => {
    setEditingSds(sds);
    setEditForm({
      manufacturer: sds.manufacturer || '',
      cas_number: sds.cas_number || '',
      product_name: sds.product_name || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateMetadata = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('manufacturer', editForm.manufacturer);
      formData.append('cas_number', editForm.cas_number);
      formData.append('product_name', editForm.product_name);
      
      await axiosInstance.patch(`/sds/${editingSds.id}/metadata`, formData);
      
      toast.success('SDS metadata updated successfully');
      setShowEditDialog(false);
      setEditingSds(null);
      loadSDSLibrary();
    } catch (error) {
      console.error('Failed to update SDS:', error);
      toast.error(error.response?.data?.detail || 'Failed to update SDS metadata');
    }
  };

  // Handle page-level drop (outside dialog)
  const handlePageDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    // Only allow admins to upload
    if (!currentUser || (currentUser.role !== 'company_admin' && currentUser.role !== 'super_admin')) {
      toast.error('Only admins can upload SDS documents');
      return;
    }
    
    console.log('Page drop event triggered');
    toast.info('Processing dropped files...');
    
    try {
      const items = e.dataTransfer.items;
      console.log('DataTransfer items:', items ? items.length : 0);
      
      let pdfFiles = [];
      
      if (!items || items.length === 0) {
        // Fallback to files if items not available
        const files = Array.from(e.dataTransfer.files);
        pdfFiles = files.filter(file => 
          file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
        );
      } else {
        // Get all file entries (handles folders recursively)
        const fileEntries = await getAllFileEntries(items);
        
        // Convert file entries to actual File objects
        const filePromises = fileEntries.map(entry => {
          return new Promise((resolve, reject) => {
            entry.file(resolve, reject);
          });
        });
        
        const allFiles = await Promise.all(filePromises);
        pdfFiles = allFiles.filter(file => 
          file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
        );
      }
      
      if (pdfFiles.length === 0) {
        toast.error('No PDF files found in the dropped folder/files');
        return;
      }
      
      // Set files and open dialog
      setUploadForm({
        ...uploadForm,
        file: null,
        files: pdfFiles,
        chemical_name: ''
      });
      
      toast.success(`📁 Found ${pdfFiles.length} PDF files - opening upload dialog...`);
      setShowUploadDialog(true);
      
    } catch (error) {
      console.error('Error processing dropped files:', error);
      toast.error(`Failed to process dropped files: ${error.message}`);
    }
  };

  const handlePageDragOver = (e) => {
    e.preventDefault();
    if (currentUser && (currentUser.role === 'company_admin' || currentUser.role === 'super_admin')) {
      setIsDragging(true);
    }
  };

  const handlePageDragLeave = (e) => {
    e.preventDefault();
    // Only reset if leaving the main container
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  return (
    <div 
      className={`container mx-auto px-4 py-8 max-w-7xl min-h-screen transition-all ${
        isDragging ? 'bg-orange-50 ring-4 ring-orange-400 ring-inset rounded-xl' : ''
      }`}
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
    >
      {/* Page-level drop overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-orange-500/20 z-40 pointer-events-none flex items-center justify-center">
          <div className="bg-white p-8 rounded-2xl shadow-2xl border-4 border-orange-500 border-dashed">
            <Upload className="w-16 h-16 mx-auto mb-4 text-orange-500 animate-bounce" />
            <p className="text-2xl font-bold text-orange-700">Drop SDS Folder Here!</p>
            <p className="text-gray-600 mt-2">Release to upload all PDFs</p>
          </div>
        </div>
      )}
      
      <div className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.history.back()}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Safety Data Sheets Library</h1>
              <p className="text-gray-600">
                Site-wide SDS repository - accessible to all companies
              </p>
            </div>
          </div>
          
          {(userRole === 'admin' || userRole === 'supervisor' || currentUser?.role === 'company_admin' || currentUser?.role === 'super_admin' || currentUser?.role === 'supervisor') && (
            <div className="flex items-center gap-2">
              {canManageSds && (
                <Button
                  variant="outline"
                  className="gap-2"
                  data-testid="sds-superseded-open-btn"
                  onClick={() => { setShowSupersededDialog(true); loadSuperseded(); }}
                >
                  <Archive className="w-4 h-4" />
                  Recently Superseded
                </Button>
              )}
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Upload className="w-4 h-4" />
                  Upload SDS
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl">Upload SDS to Site-Wide Library</DialogTitle>
                  <DialogDescription className="text-base">
                    Upload one or multiple Safety Data Sheet PDFs. Files will be accessible to all companies.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-6 mt-4">
                  {/* Drag & Drop Zone */}
                  <div 
                    className={`border-2 border-dashed rounded-xl p-10 text-center transition-all ${
                      isDragging 
                        ? 'border-orange-500 bg-orange-50 scale-[1.02]' 
                        : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/30'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="flex flex-col items-center">
                      <Upload className="w-16 h-16 mb-4 text-orange-500" />
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        Drag & Drop Your SDS Folder Here
                      </h3>
                      <p className="text-sm text-gray-600 mb-6">
                        Or click below to browse for files or folders
                      </p>
                      
                      {/* File Input Buttons */}
                      <div className="flex gap-3">
                        <Button 
                          type="button" 
                          variant="default" 
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => document.getElementById('file').click()}
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Select Files
                        </Button>
                        <Button 
                          type="button" 
                          variant="default" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => document.getElementById('folder').click()}
                        >
                          📁 Bulk Upload (Folder)
                        </Button>
                      </div>
                      
                      <Input
                        id="file"
                        type="file"
                        accept=".pdf"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <input
                        id="folder"
                        type="file"
                        webkitdirectory=""
                        directory=""
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      
                      <div className="mt-6 flex items-center gap-2 text-xs text-gray-500">
                        <span>💡</span>
                        <span>Supports nested folders • Auto-detects chemical names from filenames</span>
                      </div>
                    </div>
                  </div>
                  {uploadForm.files.length > 0 && (
                    <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">📁</span>
                          <span className="font-bold text-green-900 text-lg">
                            {uploadForm.files.length} PDF file{uploadForm.files.length !== 1 ? 's' : ''} ready to upload
                          </span>
                        </div>
                        <Badge className="bg-green-500 text-white hover:bg-green-600">
                          Ready
                        </Badge>
                      </div>
                      
                      {uploadForm.files.length > 1 && (
                        <>
                          <p className="text-sm text-green-800 mb-3 flex items-center gap-1">
                            <span>✨</span>
                            <span>Chemical names will be auto-extracted from filenames</span>
                          </p>
                          
                          <div className="bg-white rounded-lg p-4 border border-green-200">
                            <p className="font-semibold text-gray-700 mb-2 text-sm">Preview of first 5 files:</p>
                            <div className="space-y-1.5">
                              {uploadForm.files.slice(0, 5).map((file, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm">
                                  <span className="text-gray-400 flex-shrink-0">•</span>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-gray-600 break-all">{file.name}</span>
                                    <span className="text-gray-400 mx-2">→</span>
                                    <span className="text-green-700 font-semibold">{cleanFileName(file.name)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {uploadForm.files.length > 5 && (
                              <p className="text-gray-500 text-sm mt-3 text-center">
                                + {uploadForm.files.length - 5} more file{uploadForm.files.length - 5 !== 1 ? 's' : ''}...
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Optional Metadata Section */}
                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <span>📝</span>
                      <span>Additional Information (Optional)</span>
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="chemical_name" className="text-sm font-medium">
                          Chemical Name {uploadForm.files.length === 1 && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          id="chemical_name"
                          value={uploadForm.chemical_name}
                          onChange={(e) => setUploadForm({ ...uploadForm, chemical_name: e.target.value })}
                          placeholder={uploadForm.files.length > 1 ? "Optional - Leave blank for auto-detection" : "e.g., Sodium Hypochlorite"}
                          required={uploadForm.files.length === 1}
                          className="mt-1.5"
                        />
                        {uploadForm.files.length > 1 && (
                          <p className="text-xs text-gray-500 mt-1.5">
                            For bulk uploads, leave blank to auto-extract from filenames
                          </p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="manufacturer" className="text-sm font-medium">Manufacturer</Label>
                          <Input
                            id="manufacturer"
                            value={uploadForm.manufacturer}
                            onChange={(e) => setUploadForm({ ...uploadForm, manufacturer: e.target.value })}
                            placeholder="e.g., Clorox"
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="cas_number" className="text-sm font-medium">CAS Number</Label>
                          <Input
                            id="cas_number"
                            value={uploadForm.cas_number}
                            onChange={(e) => setUploadForm({ ...uploadForm, cas_number: e.target.value })}
                            placeholder="e.g., 7681-52-9"
                            className="mt-1.5"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="product_name" className="text-sm font-medium">Product Name</Label>
                        <Input
                          id="product_name"
                          value={uploadForm.product_name}
                          onChange={(e) => setUploadForm({ ...uploadForm, product_name: e.target.value })}
                          placeholder="e.g., Regular Bleach"
                          className="mt-1.5"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={uploading || uploadForm.files.length === 0}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {uploading ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Upload {uploadForm.files.length > 0 ? `${uploadForm.files.length} File${uploadForm.files.length !== 1 ? 's' : ''}` : ''}
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Input
                placeholder="Search by product name, chemical, concentration, manufacturer, or CAS number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button 
                onClick={handleSearch}
                disabled={searching}
                className="gap-2"
              >
                <Search className="w-4 h-4" />
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SDS Library Table */}
      <Card>
        <CardHeader>
          <CardTitle>Safety Data Sheets Library</CardTitle>
          <CardDescription>
            Search for SDS documents by product name, manufacturer, chemical name, or CAS number.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {searching ? (
            <div className="text-center py-12 text-gray-500">
              <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p>Searching...</p>
            </div>
          ) : sdsDocuments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              {searchQuery ? (
                <>
                  <p className="text-lg font-medium mb-2">No SDS documents found for "{searchQuery}"</p>
                  <p className="text-sm">Try a different search term</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium mb-2 text-gray-700">Search for Safety Data Sheets</p>
                  <p className="text-sm mb-4">Enter a product name, manufacturer, or chemical name above and click Search</p>
                  <p className="text-xs text-gray-400">Examples: "bleach", "Oatey", "acetone", "ChemCorp"</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Found <span className="font-semibold text-orange-600">{sdsDocuments.length}</span> result{sdsDocuments.length !== 1 ? 's' : ''} 
                  {searchQuery && <span> for "<span className="font-medium">{searchQuery}</span>"</span>}
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product Name</TableHead>
                    <TableHead>Chemical</TableHead>
                    <TableHead>Concentration</TableHead>
                    <TableHead>Manufacturer</TableHead>
                    <TableHead>CAS Number</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sdsDocuments.map((sds) => {
                    return (
                    <TableRow key={sds.id}>
                      <TableCell className="font-medium">{sds.product_name || sds.chemical_name}</TableCell>
                      <TableCell className="text-sm text-gray-600">{sds.chemical_name !== sds.product_name ? sds.chemical_name : '-'}</TableCell>
                      <TableCell>
                        {sds.concentration ? (
                          <Badge variant="outline" className="bg-blue-50">{sds.concentration}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{sds.manufacturer || '-'}</TableCell>
                      <TableCell>{sds.cas_number || '-'}</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {sds.revision_date ? new Date(sds.revision_date).toLocaleDateString() : new Date(sds.uploaded_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleView(sds)}
                            className="gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(sds)}
                            className="gap-1"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </Button>
                          {canEdit(sds) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(sds)}
                              className="gap-1"
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </Button>
                          )}
                          {canDelete(sds) && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(sds)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">🚀 Intelligent SDS Library</p>
              <p className="mb-2">
                This site-wide library features automatic deduplication, AI-powered hazard extraction, and cross-module linking:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Drag & Drop Upload:</strong> Just drag your SDS folder anywhere on this page!</li>
                <li><strong>Smart Deduplication:</strong> System keeps only the latest version of each SDS</li>
                <li><strong>Auto Hazard Extraction:</strong> AI extracts hazards, PPE, and safety info from PDFs</li>
                <li><strong>Cross-Module Linking:</strong> Automatically links to JSAs, Incidents, and Audits that mention the chemical</li>
                <li><strong>Shared Globally:</strong> Upload once, available to all companies on the platform</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit SDS Metadata Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit SDS Metadata</DialogTitle>
            <DialogDescription>
              Update manufacturer, CAS number, or product name for {editingSds?.chemical_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateMetadata} className="space-y-4">
            <div>
              <Label htmlFor="edit_manufacturer">Manufacturer</Label>
              <Input
                id="edit_manufacturer"
                value={editForm.manufacturer}
                onChange={(e) => setEditForm({ ...editForm, manufacturer: e.target.value })}
                placeholder="e.g., Clorox Company"
              />
            </div>
            <div>
              <Label htmlFor="edit_cas_number">CAS Number</Label>
              <Input
                id="edit_cas_number"
                value={editForm.cas_number}
                onChange={(e) => setEditForm({ ...editForm, cas_number: e.target.value })}
                placeholder="e.g., 7681-52-9"
              />
            </div>
            <div>
              <Label htmlFor="edit_product_name">Product Name (Optional)</Label>
              <Input
                id="edit_product_name"
                value={editForm.product_name}
                onChange={(e) => setEditForm({ ...editForm, product_name: e.target.value })}
                placeholder="e.g., Regular Bleach"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Recently Superseded Dialog */}
      <Dialog open={showSupersededDialog} onOpenChange={setShowSupersededDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="sds-superseded-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              Recently Superseded SDS
            </DialogTitle>
            <DialogDescription>
              When a newer revision of an SDS is uploaded, the older version is archived here instead of deleted. You can restore an archived version if the current active SDS was uploaded in error.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {loadingSuperseded ? (
              <div className="text-center py-10 text-gray-500">Loading...</div>
            ) : supersededDocs.length === 0 ? (
              <div className="text-center py-10 text-gray-500" data-testid="sds-superseded-empty">
                No superseded SDS documents. All uploaded SDS files are currently active.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product / Chemical</TableHead>
                    <TableHead>Manufacturer</TableHead>
                    <TableHead>Revision Date</TableHead>
                    <TableHead>Superseded On</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supersededDocs.map((d) => {
                    const label = d.product_name || d.chemical_name || 'Untitled SDS';
                    const rev = d.revision_date ? new Date(d.revision_date).toLocaleDateString() : '-';
                    const supAt = d.superseded_at ? new Date(d.superseded_at).toLocaleDateString() : '-';
                    return (
                      <TableRow key={d.id} data-testid={`sds-superseded-row-${d.id}`}>
                        <TableCell className="font-medium">{label}</TableCell>
                        <TableCell>{d.manufacturer || '-'}</TableCell>
                        <TableCell>{rev}</TableCell>
                        <TableCell>{supAt}</TableCell>
                        <TableCell className="text-sm text-gray-600">{d.superseded_reason || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`sds-superseded-download-${d.id}`}
                              onClick={() => window.open(`${axiosInstance.defaults.baseURL}/sds/download/${d.id}`, '_blank')}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`sds-superseded-restore-${d.id}`}
                              onClick={() => handleRestoreSuperseded(d.id, label)}
                            >
                              Restore
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SDSLookup;
